import projectConfig, { PROJECT } from '~/projects/config';
import justdoneRuntimeBehavior from '~/projects/justdone/runtimeBehavior';
import polyllmChatRuntimeBehavior from '~/projects/polyllm-chat/runtimeBehavior';
import sidekickerRuntimeBehavior from '~/projects/sidekicker/runtimeBehavior';
import {
  PaymentByRegionConfig,
  PaymentConfig,
  PersonalInformationLegalLink,
  ProjectRuntimeBehavior,
  SubscriptionModalComponent,
} from '~/projects/types';
import { SUBSCRIPTION_STATUS } from '~/types/subscription';
import { resolvePaymentConfigForCountry } from '~/utils/resolvePaymentConfigForCountry';

export type {
  PaymentByRegionConfig,
  PaymentConfig,
  ProjectRuntimeBehavior,
  SubscriptionModalComponent,
};

const BY_PROJECT: Record<PROJECT, ProjectRuntimeBehavior> = {
  [PROJECT.JUSTDONE]: justdoneRuntimeBehavior,
  [PROJECT.SIDEKICKER]: sidekickerRuntimeBehavior,
  [PROJECT.POLYLLM_CHAT]: polyllmChatRuntimeBehavior,
};

/**
 * Per-project runtime behavior so feature code does not scatter
 * `projectConfig.project === PROJECT.*` checks.
 */
export class ProjectBehaviorStrategy {
  static get current(): ProjectRuntimeBehavior {
    return BY_PROJECT[projectConfig.project];
  }

  static get shouldFetchSubscriptionEndpoint(): boolean {
    return this.current.fetchSubscriptionEndpoint;
  }

  static get shouldCreateFunnelUserBeforeSignUp(): boolean {
    return this.current.createFunnelUserBeforeSignUp;
  }

  static get shouldFetchTrustpilotInviteUrl(): boolean {
    return this.current.fetchTrustpilotInviteUrl;
  }

  static get shouldUseProfileSubscriptionOnly(): boolean {
    return this.current.subscriptionFromProfileOnly;
  }

  static get shouldShowSubscriptionUsageBlocks(): boolean {
    return this.current.showSubscriptionUsageBlocks;
  }

  static get shouldShowCancelRetentionModal(): boolean {
    return this.current.showCancelRetentionModal;
  }

  /** OnboardingMiddleware enforces redirect to onboarding when user is not onboarded. */
  static get isWithOnboardingMiddleware(): boolean {
    return this.current.withOnboardingMiddleware;
  }

  static get shouldUseSettingsMainLayoutNoSidebar(): boolean {
    return this.current.settingsMainLayoutNoSidebar;
  }

  static get shouldLoadDesktopSidebarThemeToggle(): boolean {
    return this.current.loadDesktopSidebarThemeToggle;
  }

  static get shouldUseGpt51AsDefaultChatVendor(): boolean {
    return this.current.chatVendorDefaultIsGpt51;
  }

  static get isChatVendorSelectorAvailableWithoutFeatureFlag(): boolean {
    return this.current.chatVendorSelectorAvailableWithoutFeatureFlag;
  }

  static get usesChatGptShellLayout(): boolean {
    return this.current.chatUsesGptShellLayout;
  }

  static get supportsAnonymousChatSessions(): boolean {
    return this.current.chatAnonymousSessionsEnabled;
  }

  static get shouldUseChatwithaiEndpoints(): boolean {
    return this.current.chatUsesChatwithaiEndpoints;
  }

  static get siteTitle(): string {
    return this.current.title;
  }

  /** React-router `basename` for the current URL (PolyLLM locale prefix; `undefined` elsewhere). */
  static get routerBasename(): string | undefined {
    return this.current.getRouterBasename?.();
  }

  static get shouldShowFrozenSubscriptionBlock(): boolean {
    return this.current.showFrozenSubscriptionBlock;
  }

  static get shouldShowHomeRetentionPopover(): boolean {
    return this.current.showHomeRetentionPopover;
  }

  static get shouldShowTrustpilotMentions(): boolean {
    return this.current.showTrustpilotMentions;
  }

  static get shouldOpenTrustpilotOnChatLike(): boolean {
    return this.current.openTrustpilotOnChatLike;
  }

  /**
   * Project-aware predicate that decides whether a backend error message
   * should bypass the shared error switch and open the project's upgrade
   * paywall. PolyLLM-Chat returns `true` for a fixed reasons set;
   * JustDone / Sidekicker always return `false`.
   */
  static shouldShowUpgradePaywallOnHandledBackendError(
    errorMessage: string,
  ): boolean {
    return this.current.shouldShowUpgradePaywallOnHandledBackendError(
      errorMessage,
    );
  }

  /**
   * Whether a NO_ACTIVE_SUBSCRIPTION backend error should open the upgrade
   * paywall (rather than the project's subscription modal) for the given
   * subscription status. Projects that don't opt in return `false`.
   */
  static shouldShowUpgradePaywallForInactiveSubscriptionStatus(
    status: SUBSCRIPTION_STATUS | undefined,
  ): boolean {
    return (
      this.current.shouldShowUpgradePaywallForInactiveSubscriptionStatus?.(
        status,
      ) ?? false
    );
  }

  /**
   * Project-specific modal rendered by `useHandleSpecialBackendErrors`
   * for subscription error states. PolyLLM-Chat injects a restore-aware
   * delegator; JustDone / Sidekicker fall back to the shared
   * `ChooseSubscriptionModal`.
   */
  static get SubscriptionModal(): SubscriptionModalComponent {
    return this.current.SubscriptionModal;
  }

  static get shouldSendDeviceIdOnSignUp(): boolean {
    return this.current.signUpUsesDeviceIdHeader;
  }

  static get shouldShowNoActiveSubscriptionModal(): boolean {
    return this.current.showNoActiveSubscriptionModal;
  }

  static get usesPolyllmRedemptionCheckout(): boolean {
    return this.current.usesPolyllmRedemptionCheckout;
  }

  static get enforcesFrontendChatUsageLimits(): boolean {
    return this.current.enforcesFrontendChatUsageLimits ?? false;
  }

  static get isDeleteAccountAvailableForAllUsers(): boolean {
    return this.current.deleteAccountAvailableForAllUsers ?? false;
  }

  static getPersonalInformationLegalLinksByCountry(
    countryCode: string,
  ): ReadonlyArray<PersonalInformationLegalLink> {
    const linksByRegion = this.current.personalInformationLegalLinksByRegion;

    return linksByRegion[countryCode] || linksByRegion.default;
  }

  static getPaymentConfigByCountry(countryCode: string): PaymentConfig {
    return resolvePaymentConfigForCountry(
      this.current.paymentConfigByRegion,
      countryCode,
    );
  }
}
